<template>
  <Teleport to="body">
    <Transition name="ebook-modal">
      <div v-if="isOpen" class="fixed inset-0 z-[9999] flex flex-col bg-gray-900/95" tabindex="0" ref="modalRef">
        
        <!-- Top Bar -->
        <div class="flex items-center justify-between px-4 py-3 bg-black/40 backdrop-blur-sm shrink-0">
          <div class="flex items-center gap-3">
            <button @click="showThumbnails = !showThumbnails" class="text-white hover:text-teal-400 transition-colors text-lg" title="สารบัญ">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            </button>
            <div class="text-white font-semibold text-sm">📖 E-Book — Cluster SIE</div>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-gray-300 text-xs">
              <template v-if="isMobile">
                <template v-if="mobilePageIndex === 0">ปก</template>
                <template v-else>หน้า {{ mobilePageIndex }} / {{ pages.length - 1 }}</template>
              </template>
              <template v-else>
                <template v-if="spreadIndex === 0">ปก</template>
                <template v-else>
                  หน้า {{ currentLeftNum }}<template v-if="currentRight"> – {{ currentRightNum }}</template> / {{ pages.length }}
                </template>
              </template>
            </span>
            <button @click="close" class="text-white hover:text-red-400 transition-colors text-2xl leading-none px-2" title="ปิด">✕</button>
          </div>
        </div>

        <!-- Thumbnail Sidebar -->
        <Transition name="thumb-slide">
          <div v-if="showThumbnails" class="absolute top-0 left-0 w-64 h-full z-[50] flex flex-col bg-gray-900/95 backdrop-blur-sm border-r border-gray-700 shadow-2xl">
            <div class="flex items-center justify-between px-4 py-3 border-b border-gray-700 shrink-0">
              <span class="text-white text-sm font-semibold">📑 สารบัญ</span>
              <button @click="showThumbnails = false" class="text-gray-400 hover:text-white text-lg">✕</button>
            </div>
            <div class="flex-1 overflow-y-auto p-3 space-y-2">
              <div 
                v-for="(page, idx) in pages" 
                :key="idx"
                @click="goToPage(idx)"
                class="relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:border-teal-400"
                :class="isActiveThumbnail(idx) ? 'border-teal-400 ring-2 ring-teal-400/30' : 'border-gray-600'"
              >
                <img :src="page" :alt="idx === 0 ? 'ปก' : 'หน้า ' + idx" class="w-full aspect-[3/4] object-cover bg-gray-800" loading="lazy">
                <div class="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs text-center py-1">
                  {{ idx === 0 ? 'ปก' : 'หน้า ' + idx }}
                </div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Book Area -->
        <div class="flex-1 flex items-center justify-center p-2 md:p-4 overflow-hidden min-h-0">
          
          <!-- ===== MOBILE: Single Page Mode ===== -->
          <template v-if="isMobile">
            <div class="flex justify-center items-center h-full w-full">
              <div class="relative max-h-full max-w-full overflow-hidden">
                <!-- Current Page -->
                <img 
                  :src="pages[mobilePageIndex]" 
                  :alt="mobilePageIndex === 0 ? 'ปก' : 'หน้า ' + mobilePageIndex" 
                  class="max-h-full max-w-full object-contain block" 
                  draggable="false"
                  :key="mobilePageIndex"
                >
                <!-- Swipe / Click zones -->
                <div 
                  v-if="mobilePageIndex > 0" 
                  @click="mobilePrev" 
                  class="absolute top-0 left-0 w-1/3 h-full z-[2] cursor-pointer"
                ></div>
                <div 
                  v-if="mobilePageIndex < pages.length - 1" 
                  @click="mobileNext" 
                  class="absolute top-0 right-0 w-2/3 h-full z-[2] cursor-pointer"
                ></div>
                <!-- Cover hint -->
                <div v-if="mobilePageIndex === 0" class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full whitespace-nowrap z-[3] pointer-events-none">
                  แตะเพื่อเปิดหนังสือ →
                </div>
              </div>
            </div>
          </template>

          <!-- ===== DESKTOP: Spread (Two-Page) Mode ===== -->
          <template v-else>
            <div class="flex justify-center items-center h-full w-full">
              <div class="book-container relative bg-white shadow-2xl border border-gray-600 w-full max-w-[95vw] max-h-full" style="aspect-ratio: 1.4 / 1;">
                
                <!-- Base Layer -->
                <div class="absolute inset-0 flex">
                  <!-- Left Page -->
                  <div class="relative w-1/2 h-full overflow-hidden">
                    <template v-if="spreadIndex === 0 && !isFlipping">
                    <div class="w-full h-full bg-white flex flex-col items-center justify-center p-8 text-center">
                      <div class="text-teal-500 text-sm tracking-widest uppercase mb-4">E-Book</div>
                      <h2 class="text-gray-800 text-2xl md:text-3xl font-bold leading-snug mb-3">Cluster SIE</h2>
                      <div class="w-16 h-[2px] bg-teal-500 mb-4"></div>
                      <p class="text-gray-500 text-sm md:text-base leading-relaxed max-w-xs">คู่มือการดำเนินงาน<br>ระบบคลัสเตอร์พัฒนาเศรษฐกิจฐานราก</p>
                      </div>
                    </template>
                    <template v-else>
                      <img v-if="displayLeft" :src="displayLeft" class="w-full h-full object-contain bg-white" draggable="false">
                      <div v-else class="w-full h-full bg-gray-100"></div>
                    </template>
                    <div class="absolute top-0 right-0 w-8 h-full bg-gradient-to-l from-black/10 to-transparent pointer-events-none z-[5]"></div>
                    <div 
                      v-if="!isFlipping && spreadIndex >= 1" 
                      @click="prevSpread" 
                      class="absolute inset-0 cursor-pointer hover:bg-black/5 transition-colors z-[2]"
                    ></div>
                  </div>
                  <!-- Right Page -->
                  <div class="relative w-1/2 h-full overflow-hidden">
                    <template v-if="spreadIndex === 0 && !isFlipping">
                      <img :src="pages[0]" alt="ปกหน้า" class="w-full h-full object-contain bg-white" draggable="false">
                      <div @click="nextSpread" class="absolute inset-0 cursor-pointer hover:bg-black/5 transition-colors z-[2]"></div>
                      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full whitespace-nowrap z-[3] pointer-events-none">
                        คลิกเพื่อเปิดหนังสือ →
                      </div>
                    </template>
                    <template v-else>
                      <img v-if="displayRight" :src="displayRight" class="w-full h-full object-contain bg-white" draggable="false">
                      <div v-else class="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400">— จบ —</div>
                    </template>
                    <div class="absolute top-0 left-0 w-8 h-full bg-gradient-to-r from-black/10 to-transparent pointer-events-none z-[5]"></div>
                    <div 
                      v-if="!isFlipping && canGoNext && spreadIndex > 0" 
                      @click="nextSpread" 
                      class="absolute inset-0 cursor-pointer hover:bg-black/5 transition-colors z-[2]"
                    ></div>
                  </div>
                </div>

                <!-- Center Spine -->
                <div class="absolute left-1/2 -translate-x-1/2 top-0 w-[2px] h-full bg-gradient-to-b from-gray-300 via-gray-400 to-gray-300 z-[15] pointer-events-none"></div>

                <!-- Flipping Page (3D overlay) -->
                <div 
                  v-if="isFlipping" 
                  class="flip-page"
                  :class="flipAnimClass"
                >
                  <div class="flip-face flip-front">
                    <img v-if="flipFront" :src="flipFront" class="w-full h-full object-contain bg-white" draggable="false">
                    <div v-else class="w-full h-full bg-gray-50"></div>
                    <div class="absolute top-0 left-0 w-6 h-full bg-gradient-to-r from-black/10 to-transparent pointer-events-none"></div>
                  </div>
                  <div class="flip-face flip-back">
                    <img v-if="flipBack" :src="flipBack" class="w-full h-full object-contain bg-white" draggable="false">
                    <div v-else class="w-full h-full bg-gray-50"></div>
                    <div class="absolute top-0 right-0 w-6 h-full bg-gradient-to-l from-black/10 to-transparent pointer-events-none"></div>
                  </div>
                </div>

              </div>
            </div>
          </template>
        </div>

        <!-- Bottom Navigation -->
        <div class="flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 bg-black/40 backdrop-blur-sm shrink-0">
          <!-- Mobile nav -->
          <template v-if="isMobile">
            <button 
              @click="mobilePageIndex = 0" 
              :disabled="mobilePageIndex === 0"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="mobilePageIndex === 0 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'"
            >⏮</button>
            <button 
              @click="mobilePrev" 
              :disabled="mobilePageIndex === 0"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="mobilePageIndex === 0 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-500'"
            >←</button>
            <span class="text-gray-300 text-sm font-semibold min-w-[60px] text-center">
              <template v-if="mobilePageIndex === 0">ปก</template>
              <template v-else>{{ mobilePageIndex }} / {{ pages.length - 1 }}</template>
            </span>
            <button 
              @click="mobileNext" 
              :disabled="mobilePageIndex >= pages.length - 1"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="mobilePageIndex >= pages.length - 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-500'"
            >→</button>
            <button 
              @click="mobilePageIndex = pages.length - 1" 
              :disabled="mobilePageIndex >= pages.length - 1"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="mobilePageIndex >= pages.length - 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'"
            >⏭</button>
          </template>
          <!-- Desktop nav -->
          <template v-else>
            <button 
              @click="firstSpread" 
              :disabled="spreadIndex === 0 || isFlipping"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="spreadIndex === 0 || isFlipping ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'"
            >⏮ ปก</button>
            <button 
              @click="prevSpread" 
              :disabled="spreadIndex === 0 || isFlipping"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="spreadIndex === 0 || isFlipping ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-500'"
            >← ก่อนหน้า</button>
            <span class="text-gray-300 text-sm font-semibold min-w-[80px] text-center">
              <template v-if="spreadIndex === 0">ปก</template>
              <template v-else>{{ currentLeftNum }}<template v-if="currentRight">–{{ currentRightNum }}</template> / {{ pages.length }}</template>
            </span>
            <button 
              @click="nextSpread" 
              :disabled="!canGoNext || isFlipping"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="!canGoNext || isFlipping ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-500'"
            >ถัดไป →</button>
            <button 
              @click="lastSpread" 
              :disabled="!canGoNext || isFlipping"
              class="px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              :class="!canGoNext || isFlipping ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'"
            >⏭ สุดท้าย</button>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  pages: { type: Array, default: () => [] },
})

const emit = defineEmits(['close'])

const modalRef = ref(null)

// ── Responsive: detect mobile ──
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
}

// ── Mobile: single page state ──
const mobilePageIndex = ref(0)
const mobileNext = () => {
  if (mobilePageIndex.value < props.pages.length - 1) mobilePageIndex.value++
}
const mobilePrev = () => {
  if (mobilePageIndex.value > 0) mobilePageIndex.value--
}

// ── Desktop: spread state ──
const spreadIndex = ref(0)
const isFlipping = ref(false)
const flipDir = ref('')
const flipFront = ref(null)
const flipBack = ref(null)
const flipBaseLeft = ref(null)
const flipBaseRight = ref(null)
const FLIP_DURATION = 600
const showThumbnails = ref(false)

// Reset on open
watch(() => props.isOpen, (val) => {
  if (val) {
    checkMobile()
    spreadIndex.value = 0
    mobilePageIndex.value = 0
    isFlipping.value = false
    flipDir.value = ''
    document.body.style.overflow = 'hidden'
    nextTick(() => modalRef.value?.focus())
  } else {
    document.body.style.overflow = ''
  }
})

// Helper: get spread pages by index
const getSpreadPages = (idx) => {
  if (idx <= 0) return { left: null, right: props.pages[0] || null }
  const li = 1 + (idx - 1) * 2
  return {
    left: props.pages[li] || null,
    right: props.pages[li + 1] || null,
  }
}

// Total spreads
const totalSpreads = computed(() => {
  if (props.pages.length <= 1) return 1
  return 1 + Math.ceil((props.pages.length - 1) / 2)
})

// Current spread pages
const currentLeft = computed(() => getSpreadPages(spreadIndex.value).left)
const currentRight = computed(() => getSpreadPages(spreadIndex.value).right)

// Page numbers
const currentLeftNum = computed(() => {
  if (spreadIndex.value <= 0) return 0
  return 1 + (spreadIndex.value - 1) * 2 + 1
})
const currentRightNum = computed(() => {
  if (spreadIndex.value <= 0) return 0
  return 1 + (spreadIndex.value - 1) * 2 + 2
})

// Display pages: during flip use flip base state, otherwise current
const displayLeft = computed(() => isFlipping.value ? flipBaseLeft.value : currentLeft.value)
const displayRight = computed(() => isFlipping.value ? flipBaseRight.value : currentRight.value)

const canGoNext = computed(() => spreadIndex.value < totalSpreads.value - 1)

// Flip animation class
const flipAnimClass = computed(() => {
  if (flipDir.value === 'next') return 'flip-forward'
  if (flipDir.value === 'prev') return 'flip-backward'
  return ''
})

// ── Navigation ──
const nextSpread = () => {
  if (isFlipping.value || !canGoNext.value) return

  if (spreadIndex.value === 0) {
    // Cover → Spread 1: flip the cover (right page) to reveal spread 1
    const next = getSpreadPages(1)
    flipBaseLeft.value = null  // title page stays during flip
    flipBaseRight.value = next.right
    flipFront.value = props.pages[0]  // cover image on front
    flipBack.value = next.left        // first spread left on back

    flipDir.value = 'next'
    isFlipping.value = true

    setTimeout(() => {
      spreadIndex.value = 1
      isFlipping.value = false
      flipDir.value = ''
    }, FLIP_DURATION)
    return
  }

  // Spread → Spread: 3D page flip
  const curr = getSpreadPages(spreadIndex.value)
  const next = getSpreadPages(spreadIndex.value + 1)

  flipBaseLeft.value = curr.left
  flipBaseRight.value = next.right
  flipFront.value = curr.right
  flipBack.value = next.left

  flipDir.value = 'next'
  isFlipping.value = true

  setTimeout(() => {
    spreadIndex.value++
    isFlipping.value = false
    flipDir.value = ''
  }, FLIP_DURATION)
}

const prevSpread = () => {
  if (isFlipping.value || spreadIndex.value <= 0) return

  if (spreadIndex.value === 1) {
    // Spread 1 → Cover: flip back to reveal cover
    const curr = getSpreadPages(1)
    flipBaseLeft.value = null  // title page visible underneath
    flipBaseRight.value = curr.right
    flipFront.value = props.pages[0]  // cover on front (destination)
    flipBack.value = curr.left        // spread 1 left on back (current)

    flipDir.value = 'prev'
    isFlipping.value = true

    setTimeout(() => {
      spreadIndex.value = 0
      isFlipping.value = false
      flipDir.value = ''
    }, FLIP_DURATION)
    return
  }

  // Spread → Spread: 3D page flip (reverse)
  const curr = getSpreadPages(spreadIndex.value)
  const prev = getSpreadPages(spreadIndex.value - 1)

  flipBaseLeft.value = prev.left
  flipBaseRight.value = curr.right
  flipFront.value = prev.right
  flipBack.value = curr.left

  flipDir.value = 'prev'
  isFlipping.value = true

  setTimeout(() => {
    spreadIndex.value--
    isFlipping.value = false
    flipDir.value = ''
  }, FLIP_DURATION)
}

const firstSpread = () => {
  if (isFlipping.value) return
  spreadIndex.value = 0
}

const lastSpread = () => {
  if (isFlipping.value) return
  spreadIndex.value = totalSpreads.value - 1
}

const close = () => emit('close')

// Thumbnail navigation
const goToPage = (pageIdx) => {
  if (isFlipping.value) return
  if (isMobile.value) {
    mobilePageIndex.value = pageIdx
  } else {
    if (pageIdx === 0) {
      spreadIndex.value = 0
    } else {
      spreadIndex.value = 1 + Math.floor((pageIdx - 1) / 2)
    }
  }
  showThumbnails.value = false
}

const isActiveThumbnail = (idx) => {
  if (isMobile.value) return mobilePageIndex.value === idx
  if (spreadIndex.value === 0) return idx === 0
  const li = 1 + (spreadIndex.value - 1) * 2
  return idx === li || idx === li + 1
}

// Keyboard navigation
const handleKeydown = (e) => {
  if (!props.isOpen) return
  switch (e.key) {
    case 'ArrowRight':
    case ' ':
      e.preventDefault()
      isMobile.value ? mobileNext() : nextSpread()
      break
    case 'ArrowLeft':
      e.preventDefault()
      isMobile.value ? mobilePrev() : prevSpread()
      break
    case 'Home':
      e.preventDefault()
      if (isMobile.value) mobilePageIndex.value = 0
      else firstSpread()
      break
    case 'End':
      e.preventDefault()
      if (isMobile.value) mobilePageIndex.value = props.pages.length - 1
      else lastSpread()
      break
    case 'Escape':
      close()
      break
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', checkMobile)
})
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', checkMobile)
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* ── Book container: 3D perspective context ── */
.book-container {
  perspective: 2500px;
}

/* ── Flipping page: 3D overlay positioned on the right half ── */
.flip-page {
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  transform-style: preserve-3d;
  transform-origin: left center; /* hinge at the spine */
  z-index: 30;
}

/* ── Page faces ── */
.flip-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  overflow: hidden;
}

.flip-front {
  z-index: 2;
}

.flip-back {
  z-index: 1;
  transform: rotateY(180deg);
}

/* ── Forward flip: right page turns to the left (0° → -180°) ── */
.flip-forward {
  animation: flipForward 0.6s ease-in-out forwards;
}

/* ── Backward flip: page returns from left to right (-180° → 0°) ── */
.flip-backward {
  animation: flipBackward 0.6s ease-in-out forwards;
}

@keyframes flipForward {
  0% {
    transform: rotateY(0deg);
  }
  100% {
    transform: rotateY(-180deg);
  }
}

@keyframes flipBackward {
  0% {
    transform: rotateY(-180deg);
  }
  100% {
    transform: rotateY(0deg);
  }
}

/* ── Thumbnail sidebar slide ── */
.thumb-slide-enter-active {
  transition: transform 0.3s ease-out;
}
.thumb-slide-leave-active {
  transition: transform 0.2s ease-in;
}
.thumb-slide-enter-from,
.thumb-slide-leave-to {
  transform: translateX(-100%);
}

/* ── Modal entrance / exit ── */
.ebook-modal-enter-active {
  transition: all 0.3s ease-out;
}
.ebook-modal-leave-active {
  transition: all 0.2s ease-in;
}
.ebook-modal-enter-from,
.ebook-modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
